import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation, Navigate } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { CartUiProvider } from './context/CartUiContext';
import { AuthProvider } from './context/AuthContext';
import { SecurityProvider } from './context/SecurityContext';
import { CompanyProvider } from './context/CompanyContext';
import { useModalCleanup } from './hooks/useModalCleanup';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import AdminBanner from './components/AdminBanner';
import LoadingSpinner from './components/LoadingSpinner';
import SoftPageOutlet from './components/SoftPageOutlet';

const Home = lazy(() => import('./pages/Home'));
const Catalogo = lazy(() => import('./pages/Catalogo'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Carrito = lazy(() => import('./pages/Carrito'));
const Checkout = lazy(() => import('./pages/Checkout'));
const SobreNosotros = lazy(() => import('./pages/SobreNosotros'));
const Contacto = lazy(() => import('./pages/Contacto'));
const Login = lazy(() => import('./pages/Login'));
const Registro = lazy(() => import('./pages/Registro'));
const MiCuenta = lazy(() => import('./pages/MiCuenta'));
const AdminLogin = lazy(() => import('./pages/Admin/Login'));
const AdminCambiarContrasena = lazy(() => import('./pages/Admin/AdminCambiarContrasena'));
const AdminDashboard = lazy(() => import('./pages/Admin/Dashboard'));
const AdminProductos = lazy(() => import('./pages/Admin/Productos'));
const AdminContacto = lazy(() => import('./pages/Admin/Contacto'));
const AdminCompras = lazy(() => import('./pages/Admin/Compras'));
const AdminUsuariosAdmin = lazy(() => import('./pages/Admin/UsuariosAdmin'));
const AdminConsultorios = lazy(() => import('./pages/Admin/Consultorios'));
const AdminLogs = lazy(() => import('./pages/Admin/Logs'));
const AdminCarritosAbandonados = lazy(() => import('./pages/Admin/CarritosAbandonados'));
const AdminFacturas = lazy(() => import('./pages/Admin/Facturas'));
const AdminNewsletter = lazy(() => import('./pages/Admin/Newsletter'));
import AdminRouteWrapper from './components/Admin/AdminRouteWrapper';
import WhatsAppFloatingButton from './components/WhatsAppFloatingButton';
import InactivityLogout from './components/InactivityLogout';
import ScrollToTop from './components/ScrollToTop';
import ConsultorioRouteWrapper from './components/Consultorio/ConsultorioRouteWrapper';
const ConsultorioDashboard = lazy(() => import('./pages/Consultorio/ConsultorioDashboard'));
const Pacientes = lazy(() => import('./pages/Consultorio/Pacientes'));
const Citas = lazy(() => import('./pages/Consultorio/Citas'));
const PacienteDetail = lazy(() => import('./pages/Consultorio/PacienteDetail'));
const ProductosConsultorio = lazy(() => import('./pages/Consultorio/ProductosConsultorio'));
const FacturasConsultorio = lazy(() => import('./pages/Consultorio/FacturasConsultorio'));
const UsuariosConsultorio = lazy(() => import('./pages/Consultorio/UsuariosConsultorio'));
const PerfilProfesional = lazy(() => import('./pages/Consultorio/PerfilProfesional'));
const PresentacionConsultorio = lazy(() => import('./pages/Consultorio/PresentacionConsultorio'));
import CambiarContrasena from './pages/CambiarContrasena';
import NotFound from './pages/NotFound';

const AUTH_FULL_PAGE_PATHS = ['/admin/login', '/cambiar-contrasena'];

function DefaultLayout() {
  const location = useLocation();
  const isAdminArea = location.pathname.startsWith('/admin');
  const isConsultorioArea = location.pathname.startsWith('/consultorio');
  const isAuthFullPage = AUTH_FULL_PAGE_PATHS.includes(location.pathname);

  if (isAuthFullPage) {
    return (
      <div className="storefront-root np-auth-layout">
        <Outlet />
      </div>
    );
  }

  const showStorefrontChrome = !isAdminArea && !isConsultorioArea;

  return (
    <div className={showStorefrontChrome ? 'storefront-root d-flex flex-column min-vh-100 w-100 m-0 p-0' : 'd-flex flex-column min-vh-100 w-100 m-0 p-0'}>
      {showStorefrontChrome && <Navbar />}
      <div className={`flex-grow-1 d-flex flex-column min-vh-0${showStorefrontChrome ? ' sf-storefront-main' : ''}`}>
        {showStorefrontChrome && <AdminBanner />}
        <main className="flex-grow-1">
          <Suspense fallback={<LoadingSpinner />}>
            <SoftPageOutlet />
          </Suspense>
        </main>
        {showStorefrontChrome && <Footer />}
        {showStorefrontChrome && <WhatsAppFloatingButton />}
      </div>
    </div>
  );
}

function AppContent() {
  useModalCleanup();

  return (
    <>
      <ScrollToTop />
      <InactivityLogout />
      <Routes>
      <Route path="/consultorio" element={<ConsultorioRouteWrapper />}>
        <Route index element={<ConsultorioDashboard />} />
        <Route path="pacientes" element={<Pacientes />} />
        <Route path="citas" element={<Citas />} />
        <Route path="paciente/:id" element={<PacienteDetail />} />
        <Route path="productos" element={<ProductosConsultorio />} />
        <Route path="facturas" element={<FacturasConsultorio />} />
        <Route path="usuarios" element={<UsuariosConsultorio />} />
        <Route path="perfil" element={<PerfilProfesional />} />
      </Route>

      <Route path="/" element={<DefaultLayout />}>
        <Route index element={<Home />} />
        <Route path="catalogo" element={<Catalogo />} />
        <Route path="producto/:id" element={<ProductDetail />} />
        <Route path="carrito" element={<Carrito />} />
        <Route path="checkout" element={<Checkout />} />
        <Route path="sobre-nosotros" element={<SobreNosotros />} />
        <Route path="caracteristicas-consultorio" element={<PresentacionConsultorio />} />
        <Route path="contacto" element={<Contacto />} />
        <Route path="login" element={<Login />} />
        <Route path="registro" element={<Registro />} />
        <Route path="cambiar-contrasena" element={<CambiarContrasena />} />
        <Route path="mi-cuenta" element={<MiCuenta />} />
        <Route path="admin" element={<AdminRouteWrapper />}>
          <Route path="login" element={<AdminLogin />} />
          <Route path="cambiar-contrasena" element={<AdminCambiarContrasena />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="productos" element={<AdminProductos />} />
          <Route path="compras" element={<AdminCompras />} />
          <Route path="carritos-abandonados" element={<AdminCarritosAbandonados />} />
          <Route path="facturas" element={<AdminFacturas />} />
          <Route path="contacto" element={<AdminContacto />} />
          <Route path="newsletter" element={<AdminNewsletter />} />
          <Route path="usuarios-admin" element={<AdminUsuariosAdmin />} />
          <Route path="consultorios" element={<AdminConsultorios />} />
          <Route path="logs" element={<AdminLogs />} />
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
    </>
  );
}

function App() {
  return (
    <CartProvider>
      <CartUiProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <CompanyProvider>
            <AuthProvider>
              <SecurityProvider>
                <AppContent />
              </SecurityProvider>
            </AuthProvider>
          </CompanyProvider>
        </Router>
      </CartUiProvider>
    </CartProvider>
  );
}

export default App;
